import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CMPStep4OperationalManagement = ({ project, onComplete, onBack }) => {
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      await axios.put(`${API}/projects/${project.id}/step`, {
        step_number: 5
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onComplete({ ...project, current_step: 5 });
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {/* Back button at top */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Operational Management</h2>
        <p className="text-gray-600 mb-8">
          Review the operational management procedures that will be included in your CMP.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Daily Operations */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              5.1 Daily Operations
            </h3>
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                Daily site operations will be managed to ensure works are undertaken in an orderly, 
                safe, and environmentally responsible manner.
              </p>
              <p>
                All activities will be carried out in accordance with approved plans, permit conditions, 
                and relevant legislation. The site will be maintained in a clean and safe condition.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li>Works occur within approved construction hours</li>
                <li>No out-of-hours works without approval</li>
                <li>Higher-impact activities managed according to local laws and guidelines</li>
              </ul>
            </div>
          </div>

          {/* Deliveries and Logistics */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              5.2 Deliveries and Logistics
            </h3>
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                Deliveries to and from the site will be managed to minimise disruption to surrounding 
                properties, pedestrians, and the local road network.
              </p>
              <p>
                Delivery times will be coordinated to align with approved construction hours and site capacity. 
                Vehicles will access the site via approved routes, and on-site loading and unloading will be 
                managed to avoid obstruction of public roads or footpaths.
              </p>
            </div>
          </div>

          {/* Site Demobilisation */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              5.3 Site Demobilisation
            </h3>
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                Upon completion of construction works, the site will be demobilised in an orderly manner.
              </p>
              <p>
                Temporary structures, hoarding, plant, equipment, and surplus materials will be removed. 
                All waste will be appropriately managed and disposed of in accordance with relevant regulations.
              </p>
              <p>
                The site and adjoining public areas will be left in a safe, clean, and stable condition.
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Standard Management Procedures</p>
                <p className="mt-1 text-blue-700">
                  These operational management procedures are standard requirements that will be 
                  included in your CMP to ensure compliant site operations.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMPStep4OperationalManagement;
